<template>
  <div class="home-view container">
    <section class="home-view__hero">
      <div class="home-view__copy">
        <p class="home-view__eyebrow">{{ hasPermission('READFORM') ? 'CLIENTES' : 'INICIO' }}</p>
        <h1 class="home-view__title">{{ hasPermission('READFORM') ? 'Clientes' : canViewBirthdays ? 'Cumpleaños de socios' : 'Inicio' }}</h1>
        <p class="home-view__subtitle">
          {{ hasPermission('READFORM') ? 'Directorio corporativo con acceso rápido a cada ficha.' : canViewBirthdays ? 'Consulta los próximos cumpleaños de socios.' : 'Consulta las funciones disponibles para tu cuenta.' }}
        </p>
      </div>

      <div v-if="hasPermission('READFORM')" class="home-view__search-wrap">
        <div class="home-view__search-grid">
          <input
            v-model="searchText"
            class="input home-view__search home-view__search--wide"
            type="search"
            name="company-search"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            placeholder="Búsqueda por Rut o Nombre"
          >
        </div>

        <div class="home-view__status-row">
          <div class="home-view__status-filters">
            <button
              type="button"
              class="home-view__status-chip"
              :class="{ 'is-active': selectedStatus === '' }"
              @click="applyStatusFilter('')"
            >
              Todos
            </button>
            <button
              v-for="status in companyStatuses"
              :key="status"
              type="button"
              class="home-view__status-chip"
              :class="[getStatusChipClass(status), { 'is-active': selectedStatus === status }]"
              @click="applyStatusFilter(status)"
            >
              {{ status }}
            </button>
          </div>

          <button type="button" class="home-view__advanced-toggle" @click="showAdvancedFilters = !showAdvancedFilters">
            {{ showAdvancedFilters ? 'Ocultar búsqueda avanzada' : 'Mostrar búsqueda avanzada' }}
          </button>
        </div>

        <div class="home-view__active-filters" v-if="activeAdvancedFilters.length">
          <span class="home-view__active-label">Búsqueda avanzada:</span>
          <span v-for="filter in activeAdvancedFilters" :key="filter.label" class="home-view__active-chip">
            {{ filter.label }}
          </span>
        </div>

        <div v-if="showAdvancedFilters" class="home-view__advanced-panel">
          <div class="home-view__filters-grid">
            <div class="select home-view__filter-select">
              <select v-model="selectedTaxRegimeId">
                <option :value="0">Todos los regímenes</option>
                <option v-for="taxRegime in taxRegimes" :key="taxRegime.id" :value="taxRegime.id">{{ taxRegime.name }}</option>
              </select>
            </div>
            <div class="select home-view__filter-select">
              <select v-model="selectedBillerTypeId">
                <option :value="0">Todos los tipos de facturador</option>
                <option v-for="billerType in billerTypes" :key="billerType.id" :value="billerType.id">{{ billerType.name }}</option>
              </select>
            </div>
            <input
              v-model="organizationalTypeSearch"
              class="input home-view__search"
              type="text"
              name="organizational-type-search"
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
              spellcheck="false"
              placeholder="Tipo Organizacional"
            >
          </div>
        </div>

        <div class="home-view__actions-row">
          <p v-if="searchTooShort" class="home-view__hint">
            Ingresa al menos {{ minSearchLength }} caracteres para buscar.
          </p>
          <div class="home-view__toolbar-actions">
            <div v-if="canExportSecrets()" class="home-view__export-actions">
            <button
              type="button"
              class="button home-view__export-button home-view__export-button--secondary"
              :disabled="isLoading || isExportingCompanies || searchTooShort"
              @click="exportCompanies"
            >
              {{ isExportingCompanies ? 'Exportando...' : 'Exportar clientes' }}
            </button>
            <button
              type="button"
              class="button home-view__export-button"
              :disabled="isLoading || isExporting || searchTooShort || totalCompanies === 0"
              @click="exportSecrets"
            >
              {{ isExporting ? 'Exportando...' : 'Exportar secretos' }}
            </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div v-if="canViewBirthdays" class="home-view__birthday-access">
      <button
        type="button"
        class="button home-view__birthday-trigger"
        :class="{ 'has-alert': birthdaySummary.total_partners > 0 }"
        :disabled="birthdayLoading"
        @click="openBirthdayModal"
      >
        <span class="home-view__birthday-trigger-icon"><i class="fas fa-cake-candles"></i></span>
        <span class="home-view__birthday-trigger-copy">Cumpleaños</span>
        <span v-if="birthdaySummary.total_partners > 0" class="home-view__birthday-trigger-count">{{ birthdaySummary.total_partners }}</span>
      </button>
      <p v-if="birthdayError" class="home-view__birthday-error" role="alert">{{ birthdayError }}</p>
    </div>

    <section v-if="hasPermission('READFORM') && totalCompanies > 0" class="home-view__quality-summary">
      <article class="home-view__quality-card">
        <p class="home-view__quality-eyebrow">Calidad de datos</p>
        <div class="home-view__quality-grid">
          <div class="home-view__quality-metric">
            <span class="home-view__quality-label">Promedio general</span>
            <strong class="home-view__quality-value">{{ dataQualitySummary.average_percentage }}%</strong>
          </div>
          <div class="home-view__quality-metric">
            <span class="home-view__quality-label">Clientes completos</span>
            <strong class="home-view__quality-value">{{ dataQualitySummary.complete_count }}</strong>
          </div>
          <div class="home-view__quality-metric">
            <span class="home-view__quality-label">Clientes evaluados</span>
            <strong class="home-view__quality-value">{{ dataQualitySummary.total }}</strong>
          </div>
        </div>
      </article>
    </section>

    <CompanyList
      v-if="hasPermission('READFORM')"
      :companies="companies"
      :loading="isLoading"
      :hasWritePermission="hasPermission('EDITFORM')"
      :totalCompanies="totalCompanies"
      :hasActiveSearch="hasActiveSearch"
    />

    <div v-if="hasPermission('READFORM') && totalCompanies > 0" class="home-view__págination">
      <p class="home-view__págination-copy">
        Mostrando {{ companies.length }} de {{ totalCompanies }} clientes.
      </p>
      <div class="home-view__págination-actions">
        <button
          class="button home-view__page-button"
          type="button"
          :disabled="isLoading || page <= 1"
          @click="changePage(page - 1)"
        >
          Anterior
        </button>
        <span class="home-view__page-indicator">Página {{ page }} de {{ totalPages }}</span>
        <button
          class="button home-view__page-button"
          type="button"
          :disabled="isLoading || page >= totalPages"
          @click="changePage(page + 1)"
        >
          Siguiente
        </button>
      </div>
    </div>

    <p v-else-if="!hasPermission('READFORM') && !canViewBirthdays">No tiene los permisos necesarios </p>

    <div v-if="showExportWarningModal" class="modal home-export-modal is-active">
      <div class="modal-background" @click="closeExportWarningModal"></div>
      <div class="modal-card home-export-modal__card">
        <header class="modal-card-head home-export-modal__head">
          <div>
            <p class="home-export-modal__kicker">Exportación sensible</p>
            <p class="modal-card-title home-export-modal__title">Exposición de secretos</p>
          </div>
          <button class="delete" aria-label="close" @click="closeExportWarningModal"></button>
        </header>
        <section class="modal-card-body home-export-modal__body">
          <p class="home-export-modal__copy">
            El archivo generado incluirá secretos o contraseñas en texto claro para los clientes filtrados.
          </p>
          <p class="home-export-modal__copy home-export-modal__copy--muted">
            Exporta solo si necesitas compartir esta información en Excel y tienes autorización para manejarla.
          </p>
          <textarea
            v-model="exportReason"
            class="textarea home-export-modal__textarea"
            rows="4"
            maxlength="500"
            placeholder="Motivo de la exportación"
          ></textarea>
          <input
            v-model="exportPassword"
            class="input home-export-modal__input"
            type="password"
            name="export-secrets-confirmation"
            autocomplete="new-password"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            placeholder="Confirma tu contraseña"
          >
          <p v-if="exportError" class="home-export-modal__error">{{ exportError }}</p>
        </section>
        <footer class="modal-card-foot home-export-modal__foot">
          <button class="button home-export-modal__danger" :disabled="isExporting" @click="confirmExportSecrets">
            {{ isExporting ? 'Exportando...' : 'Entiendo, exportar' }}
          </button>
          <button class="button home-export-modal__secondary" :disabled="isExporting" @click="closeExportWarningModal">Cancelar</button>
        </footer>
      </div>
    </div>

    <div v-if="showBirthdayModal" class="modal home-birthday-modal is-active">
      <div class="modal-background" @click="closeBirthdayModal"></div>
      <div class="modal-card home-birthday-modal__card">
        <header class="modal-card-head home-birthday-modal__head">
          <div>
            <p class="home-export-modal__kicker">Cumpleaños</p>
            <p class="modal-card-title home-birthday-modal__title">Socios de cumpleaños</p>
          </div>
          <button class="delete" aria-label="close" @click="closeBirthdayModal"></button>
        </header>
        <section class="modal-card-body home-birthday-modal__body">
          <div v-if="showBirthdayNotice" class="home-view__birthday-notice home-view__birthday-notice--modal">
            <div>
              <p class="home-view__birthday-notice-eyebrow">Notificacion</p>
              <p class="home-view__birthday-notice-copy">{{ birthdayNoticeMessage }}</p>
            </div>
          </div>

          <p v-if="birthdayLoading" class="home-view__birthday-empty">Cargando cumpleaños...</p>
          <p v-else-if="birthdayError" class="home-view__birthday-error">{{ birthdayError }}</p>
          <p v-else-if="!birthdayItems.length" class="home-view__birthday-empty">
            No hay socios de cumpleaños entre hoy y los proximos {{ birthdaySummary.window_days }} dias.
          </p>

          <div v-else class="home-view__birthday-list">
            <component
              v-for="item in visibleBirthdayItems"
              :is="hasPermission('READFORM') ? 'router-link' : 'article'"
              :key="item.person_id"
              class="home-view__birthday-item"
              :to="hasPermission('READFORM') ? { name: 'CompanyShow', params: { id: item.companies[0].company_id } } : undefined"
              @click.native="closeBirthdayModal"
            >
              <div class="home-view__birthday-item-main">
                <p class="home-view__birthday-partner">{{ item.partner_name }}</p>
                <p class="home-view__birthday-company">{{ getBirthdayCompaniesLabel(item) }}</p>
              </div>
              <div class="home-view__birthday-item-meta">
                <span class="home-view__birthday-date">{{ formatBirthday(item.next_birthday) }}</span>
                <span :class="['home-view__birthday-badge', item.is_today ? 'is-today' : '']">
                  {{ getBirthdayRelativeLabel(item) }}
                </span>
              </div>
            </component>

            <div v-if="hasMoreBirthdayItems" class="home-view__birthday-actions">
              <button type="button" class="home-view__birthday-toggle" @click="toggleBirthdayExpansion">
                {{ birthdayExpanded ? 'Mostrar menos' : `Ver mas (${hiddenBirthdayItemsCount})` }}
              </button>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot home-export-modal__foot">
          <button class="button home-export-modal__secondary" @click="closeBirthdayModal">Cerrar</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import CompanyList from '../components/company/CompanyList.vue'
import auth from '@/auth/index'
import { formatDateOnly } from '@/utils/date'

export default {
  name: 'Home',
  components: {
    CompanyList
  },
  data: () => ({
    companies: [],
    companyStatuses: ['Activo', 'Inactivo', 'Esporádico', 'TTG (Trámite de término de Giro)'],
    taxRegimes: [],
    billerTypes: [],
    searchText: '',
    selectedStatus: '',
    selectedTaxRegimeId: 0,
    selectedBillerTypeId: 0,
    organizationalTypeSearch: '',
    showAdvancedFilters: false,
    isLoading: false,
    lastRequestId: 0,
    debounceTimer: null,
    page: 1,
    pageSize: 21,
    totalCompanies: 0,
    dataQualitySummary: {
      average_percentage: 0,
      complete_count: 0,
      incomplete_count: 0,
      total: 0
    },
    birthdayItems: [],
    birthdaySummary: {
      total_companies: 0,
      total_partners: 0,
      today: '',
      window_days: 7
    },
    birthdayLoading: false,
    birthdayError: '',
    birthdayNoticeDismissed: false,
    birthdayExpanded: false,
    showBirthdayModal: false,
    minSearchLength: 2,
    isExporting: false,
    isExportingCompanies: false,
    showExportWarningModal: false,
    exportReason: '',
    exportPassword: '',
    exportError: ''
  }),
  computed: {
    normalizedSearchText () {
      return this.searchText.trim()
    },
    normalizedOrganizationalType () {
      return this.organizationalTypeSearch.trim()
    },
    searchTooShort () {
      return this.normalizedSearchText.length > 0 && this.normalizedSearchText.length < this.minSearchLength
    },
    totalPages () {
      return Math.max(Math.ceil(this.totalCompanies / this.pageSize), 1)
    },
    hasActiveSearch () {
      return this.normalizedSearchText.length > 0 || this.selectedStatus !== '' || this.selectedTaxRegimeId > 0 || this.selectedBillerTypeId > 0 || this.normalizedOrganizationalType.length > 0
    },
    activeAdvancedFilters () {
      const filters = []
      const taxRegime = this.taxRegimes.find(item => item.id === this.selectedTaxRegimeId)
      const billerType = this.billerTypes.find(item => item.id === this.selectedBillerTypeId)
      if (taxRegime) {
        filters.push({ label: `Régimen: ${taxRegime.name}` })
      }
      if (billerType) {
        filters.push({ label: `Facturador: ${billerType.name}` })
      }
      if (this.normalizedOrganizationalType) {
        filters.push({ label: `Organizacional: ${this.normalizedOrganizationalType}` })
      }
      return filters
    },
    showBirthdayNotice () {
      return !this.birthdayLoading && !this.birthdayError && !this.birthdayNoticeDismissed && this.birthdaySummary.total_partners > 0
    },
    canViewBirthdays () {
      return auth.canViewBirthdays()
    },
    todayBirthdayItems () {
      return this.birthdayItems.filter(item => item.is_today)
    },
    visibleBirthdayItems () {
      if (this.birthdayExpanded) return this.birthdayItems
      return this.todayBirthdayItems
    },
    hasMoreBirthdayItems () {
      return this.birthdayItems.length > this.todayBirthdayItems.length
    },
    hiddenBirthdayItemsCount () {
      return Math.max(this.birthdayItems.length - this.todayBirthdayItems.length, 0)
    },
    birthdayNoticeMessage () {
      const companyLabel = this.birthdaySummary.total_companies === 1 ? 'cliente' : 'clientes'
      const partnerLabel = this.birthdaySummary.total_partners === 1 ? 'socio' : 'socios'
      return `Hay ${this.birthdaySummary.total_partners} ${partnerLabel} de cumpleaños en ${this.birthdaySummary.total_companies} ${companyLabel} dentro de los proximos ${this.birthdaySummary.window_days} dias.`
    }
  },
  methods: {
    async getTaxRegimes () {
      if (!this.hasPermission('READFORM')) return
      this.$axios
      .get('main/getTaxRegimes')
      .then(response => {
        this.taxRegimes = response.data.taxRegimes || []
      })
      .catch(error => console.log(error))
    },
    async getBillerTypes () {
      if (!this.hasPermission('READFORM')) return
      this.$axios
      .get('main/getBillerTypes')
      .then(response => {
        this.billerTypes = response.data.billerTypes || []
      })
      .catch(error => console.log(error))
    },
    async getUpcomingPartnerBirthdays () {
      if (!this.canViewBirthdays) {
        this.clearBirthdayData()
        this.showBirthdayModal = false
        return
      }
      this.birthdayLoading = true
      this.birthdayError = ''
      try {
        const response = await this.$axios.get('company/getUpcomingPartnerBirthdays', {
          params: {
            daysAhead: 7
          }
        })
        this.birthdayItems = response.data.items || []
        this.birthdaySummary = {
          total_companies: response.data.total_companies || 0,
          total_partners: response.data.total_partners || 0,
          today: response.data.today || '',
          window_days: response.data.window_days || 7
        }
        this.birthdayNoticeDismissed = false
        this.birthdayExpanded = false
      } catch (error) {
        this.clearBirthdayData()
        if (error && error.response && error.response.status === 403) this.showBirthdayModal = false
        this.birthdayError = (error && error.response && error.response.data && error.response.data.message) || 'No fue posible cargar los cumpleaños de socios.'
        this.birthdayExpanded = false
      } finally {
        this.birthdayLoading = false
      }
    },
    async getClientes (targetPage = 1) {
      if (!this.hasPermission('READFORM')) return
      if (this.searchTooShort) {
        this.companies = []
        this.totalCompanies = 0
        this.dataQualitySummary = {
          average_percentage: 0,
          complete_count: 0,
          incomplete_count: 0,
          total: 0
        }
        this.page = 1
        this.isLoading = false
        return
      }

      const requestId = ++this.lastRequestId
      this.isLoading = true

      this.$axios
        .get('company/getCompanies', {
          params: {
            searchParam: this.normalizedSearchText,
            status: this.selectedStatus,
            taxRegimeId: this.selectedTaxRegimeId,
            billerTypeId: this.selectedBillerTypeId,
            organizationalType: this.normalizedOrganizationalType,
            page: targetPage,
            pageSize: this.pageSize
          }
        })
        .then(response => {
          if (requestId !== this.lastRequestId) return

          this.companies = response.data.companies || []
          this.totalCompanies = response.data.total || 0
          this.dataQualitySummary = response.data.data_quality_summary || {
            average_percentage: 0,
            complete_count: 0,
            incomplete_count: 0,
            total: 0
          }
          this.page = response.data.page || targetPage
          this.pageSize = response.data.pageSize || this.pageSize
        })
        .catch(error => console.log(error))
        .finally(() => {
          if (requestId === this.lastRequestId) {
            this.isLoading = false
          }
        })
    },
    scheduleSearch () {
      clearTimeout(this.debounceTimer)
      if (!this.hasPermission('READFORM')) return
      this.debounceTimer = setTimeout(() => {
        this.getClientes(1)
      }, 350)
    },
    changePage (targetPage) {
      if (targetPage < 1 || targetPage > this.totalPages || targetPage === this.page) return
      clearTimeout(this.debounceTimer)
      this.getClientes(targetPage)
    },
    applyStatusFilter (status) {
      this.selectedStatus = status
    },
    getExportParams () {
      return {
        searchParam: this.normalizedSearchText,
        status: this.selectedStatus,
        taxRegimeId: this.selectedTaxRegimeId,
        billerTypeId: this.selectedBillerTypeId,
        organizationalType: this.normalizedOrganizationalType
      }
    },
    getDownloadFilename (headers, fallbackName = 'descarga.xls') {
      const contentDisposition = headers['content-disposition'] || ''
      const match = contentDisposition.match(/filename="?([^"]+)"?/i)
      return match ? match[1] : fallbackName
    },
    async downloadExport (url, params, fallbackName) {
      const response = await this.$axios.get(url, {
        params,
        responseType: 'blob'
      })
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/vnd.ms-excel' })
      const exportUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = exportUrl
      link.download = this.getDownloadFilename(response.headers, fallbackName)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(exportUrl)
    },
    exportSecrets () {
      if (this.searchTooShort || this.totalCompanies === 0 || this.isExporting) return
      if (!this.canExportSecrets()) return
      this.exportReason = ''
      this.exportPassword = ''
      this.exportError = ''
      this.showExportWarningModal = true
    },
    closeExportWarningModal () {
      if (this.isExporting) return
      this.showExportWarningModal = false
      this.exportReason = ''
      this.exportPassword = ''
      this.exportError = ''
    },
    async confirmExportSecrets () {
      const reason = this.exportReason.trim()
      const password = this.exportPassword
      if (!reason) {
        this.exportError = 'Debes indicar un motivo para exportar secretos.'
        return
      }
      if (!password) {
        this.exportError = 'Debes confirmar tu contraseña para exportar secretos.'
        return
      }
      this.isExporting = true
      this.exportError = ''
      try {
        await this.downloadExport('company/exportSecrets', { ...this.getExportParams(), reason, password }, 'secretos_clientes.xls')
        this.showExportWarningModal = false
        this.exportReason = ''
        this.exportPassword = ''
      } catch (error) {
        this.exportError = (error && error.response && error.response.data && error.response.data.message) || 'No fue posible exportar los secretos.'
      } finally {
        this.isExporting = false
      }
    },
    async exportCompanies () {
      if (this.searchTooShort || this.isExportingCompanies) return
      if (!this.canExportSecrets()) return
      this.isExportingCompanies = true
      try {
        await this.downloadExport('company/exportActiveCompanies', this.getExportParams(), 'reporte_clientes_filtrados.xls')
      } catch (error) {
        console.log(error)
      } finally {
        this.isExportingCompanies = false
      }
    },
    getStatusChipClass (status) {
      const classMap = {
        Activo: 'is-active-tone',
        Inactivo: 'is-inactive-tone',
        'Esporádico': 'is-sporadic-tone',
        'TTG (Trámite de término de Giro)': 'is-ttg-tone'
      }
      return classMap[status] || ''
    },
    hasPermission (code) {
      return auth.hasUserPermission(code)
    },
    canExportSecrets () {
      return auth.hasUserPermission('EXPORT_SECRET_VALUE')
    },
    dismissBirthdayNotice () {
      this.birthdayNoticeDismissed = true
    },
    openBirthdayModal () {
      if (!this.canViewBirthdays) return
      this.showBirthdayModal = true
      this.getUpcomingPartnerBirthdays()
    },
    clearBirthdayData () {
      this.birthdayItems = []
      this.birthdaySummary = { total_companies: 0, total_partners: 0, today: '', window_days: 7 }
      this.birthdayExpanded = false
    },
    closeBirthdayModal () {
      this.showBirthdayModal = false
    },
    toggleBirthdayExpansion () {
      this.birthdayExpanded = !this.birthdayExpanded
    },
    formatBirthday (value) {
      return formatDateOnly(value)
    },
    getBirthdayRelativeLabel (item) {
      if (!item) return ''
      if (item.is_today) return 'Hoy'
      return item.days_until_birthday === 1 ? 'Manana' : `En ${item.days_until_birthday} dias`
    },
    getBirthdayCompaniesLabel (item) {
      if (!item || !item.companies || !item.companies.length) return 'Sin clientes asociados'
      if (item.companies.length === 1) return item.companies[0].company_name
      const [firstCompany, ...remainingCompanies] = item.companies
      return `${firstCompany.company_name} y ${remainingCompanies.length} cliente(s) mas`
    }
  },
  mounted () {
    if (this.hasPermission('READFORM')) {
      this.getTaxRegimes()
      this.getBillerTypes()
      this.getClientes(1)
    }
    if (this.canViewBirthdays) {
      this.getUpcomingPartnerBirthdays()
    }
  },
  beforeDestroy () {
    clearTimeout(this.debounceTimer)
  },
  watch: {
    searchText () {
      this.scheduleSearch()
    },
    selectedStatus () {
      this.scheduleSearch()
    },
    selectedTaxRegimeId () {
      this.scheduleSearch()
    },
    selectedBillerTypeId () {
      this.scheduleSearch()
    },
    organizationalTypeSearch () {
      this.scheduleSearch()
    }
  }
}
</script>

<style scoped>
.home-view {
  padding-top: 1.25rem;
}

.home-view__birthday-access {
  margin-bottom: 1.5rem;
}

.home-view__hero {
  margin-bottom: 1.5rem;
}

.home-view__quality-summary {
  margin-bottom: 1rem;
}

.home-view__quality-card {
  padding: 0.95rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.46);
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(255, 252, 247, 0.82), rgba(246, 239, 229, 0.72));
  box-shadow: 0 16px 34px rgba(43, 34, 24, 0.05);
}

.home-view__birthday-notice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.9rem;
  padding: 0.85rem 0.95rem;
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(255, 244, 214, 0.95), rgba(255, 235, 194, 0.9));
  border: 1px solid rgba(179, 130, 44, 0.22);
}

.home-view__birthday-notice-eyebrow {
  margin: 0 0 0.2rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.14rem;
  text-transform: uppercase;
  color: #98651d;
}

.home-view__birthday-notice-copy {
  margin: 0;
  color: #503516;
}

.home-view__birthday-notice-close {
  border: 0;
  background: transparent;
  color: #7b5522;
  font-weight: 700;
}

.home-view__birthday-notice--modal {
  margin-bottom: 0.75rem;
}

.home-view__birthday-list {
  display: grid;
  gap: 0.5rem;
}

.home-view__birthday-actions {
  display: flex;
  justify-content: center;
  padding-top: 0.1rem;
}

.home-view__birthday-toggle {
  border: 1px solid rgba(143, 117, 68, 0.16);
  border-radius: 999px;
  background: rgba(255, 252, 247, 0.86);
  color: #6f5b36;
  padding: 0.4rem 0.8rem;
  font-size: 0.76rem;
  font-weight: 700;
}

.home-view__birthday-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.7rem 0.8rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.48);
  border: 1px solid rgba(143, 117, 68, 0.12);
  color: inherit;
  transition: transform 0.16s ease, box-shadow 0.16s ease;
}

.home-view__birthday-item:hover,
.home-view__birthday-item:focus {
  color: inherit;
  transform: translateY(-1px);
  box-shadow: 0 16px 30px rgba(43, 34, 24, 0.07);
}

.home-view__birthday-company,
.home-view__birthday-partner,
.home-view__birthday-empty,
.home-view__birthday-error,
.home-view__birthday-date {
  margin: 0;
}

.home-view__birthday-company {
  font-weight: 700;
  color: #2b1f14;
  font-size: 0.92rem;
}

.home-view__birthday-partner {
  margin-top: 0.15rem;
  color: rgba(43, 31, 20, 0.72);
  font-size: 0.84rem;
}

.home-view__birthday-item-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
}

.home-view__birthday-date {
  color: #4f3f2c;
  font-size: 0.82rem;
}

.home-view__birthday-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  padding: 0.28rem 0.55rem;
  border-radius: 999px;
  background: rgba(118, 92, 45, 0.1);
  color: #6f5b36;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.03rem;
}

.home-view__birthday-badge.is-today {
  background: rgba(198, 110, 45, 0.16);
  color: #a4521d;
}

.home-view__birthday-empty {
  color: rgba(43, 31, 20, 0.7);
  font-size: 0.9rem;
  line-height: 1.45;
}

.home-view__birthday-error {
  color: #b95252;
  font-size: 0.88rem;
}

.home-view__quality-eyebrow {
  margin: 0 0 0.72rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.18rem;
  text-transform: uppercase;
  color: rgba(36, 31, 25, 0.48);
}

.home-view__quality-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}

.home-view__quality-metric {
  padding: 0.78rem 0.82rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.45);
}

.home-view__quality-label {
  display: block;
  margin-bottom: 0.18rem;
  color: rgba(36, 31, 25, 0.52);
  font-size: 0.72rem;
  letter-spacing: 0.08rem;
  text-transform: uppercase;
}

.home-view__quality-value {
  color: #1f1a15;
  font-size: 1.2rem;
}

.home-view__copy {
  text-align: center;
}

.home-view__eyebrow {
  margin: 0 0 0.35rem;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.24rem;
  color: rgba(36, 31, 25, 0.5);
}

.home-view__title {
  margin: 0;
  font-size: clamp(2rem, 4vw, 3rem);
  line-height: 1;
  color: #211c17;
}

.home-view__subtitle {
  margin: 0.7rem auto 0;
  max-width: 34rem;
  color: rgba(36, 31, 25, 0.68);
  line-height: 1.7;
}

.home-view__search-wrap {
  max-width: 980px;
  margin: 1.25rem auto 0;
}

.home-view__search-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.85rem;
}

.home-view__filters-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.85rem;
}

.home-view__search {
  height: 3rem;
  border: 1px solid rgba(120, 102, 73, 0.12);
  border-radius: 16px;
  background: rgba(255, 252, 247, 0.72);
  box-shadow: 0 12px 30px rgba(43, 34, 24, 0.05);
}

.home-view__search:focus {
  border-color: rgba(143, 117, 68, 0.4);
  box-shadow: 0 0 0 0.2rem rgba(143, 117, 68, 0.1);
}

.home-view__search::placeholder {
  color: rgba(36, 31, 25, 0.42);
}

.home-view__filter-select select {
  width: 100%;
  height: 3rem;
  border-radius: 16px;
  border: 1px solid rgba(120, 102, 73, 0.12);
  background: rgba(255, 252, 247, 0.8);
  box-shadow: 0 12px 30px rgba(43, 34, 24, 0.05);
}

.home-view__status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.9rem;
}

.home-view__status-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.home-view__active-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.55rem;
  margin-top: 0.85rem;
}

.home-view__active-label {
  font-size: 0.82rem;
  color: rgba(36, 31, 25, 0.62);
}

.home-view__active-chip {
  border-radius: 999px;
  background: rgba(249, 246, 240, 0.95);
  border: 1px solid rgba(133, 115, 88, 0.14);
  color: #5b4c39;
  padding: 0.42rem 0.75rem;
  font-size: 0.78rem;
  font-weight: 600;
}

.home-view__advanced-toggle {
  padding: 0;
  border: 0;
  background: transparent;
  color: #6f5b36;
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
}

.home-view__advanced-panel {
  margin-top: 0.75rem;
  padding: 0.95rem;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 20px;
  background: rgba(255, 252, 247, 0.55);
}

.home-view__status-chip {
  border: 1px solid rgba(123, 107, 82, 0.14);
  border-radius: 999px;
  background: rgba(255, 252, 247, 0.88);
  color: #3a3128;
  padding: 0.55rem 0.9rem;
  font-size: 0.82rem;
  font-weight: 600;
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
}

.home-view__status-chip.is-active {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(43, 34, 24, 0.08);
}

.home-view__status-chip.is-active-tone {
  border-color: rgba(85, 111, 92, 0.26);
  color: #3f5d46;
}

.home-view__status-chip.is-inactive-tone {
  border-color: rgba(120, 110, 102, 0.26);
  color: #655b55;
}

.home-view__status-chip.is-sporadic-tone {
  border-color: rgba(126, 103, 71, 0.26);
  color: #6f5a3f;
}

.home-view__status-chip.is-ttg-tone {
  border-color: rgba(97, 102, 120, 0.26);
  color: #565d72;
}

.home-view__actions-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.55rem;
}

.home-view__hint {
  margin: 0;
  color: #6f5b36;
  font-size: 0.88rem;
}

.home-view__export-button {
  border-radius: 999px;
  background: #6f5b36;
  color: #fff9ef;
}

.home-view__export-button--secondary {
  background: rgba(255, 252, 247, 0.92);
  color: #6f5b36;
  border: 1px solid rgba(111, 91, 54, 0.24);
}

.home-view__export-button[disabled] {
  opacity: 0.6;
}

.home-view__export-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.75rem;
}

.home-view__toolbar-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.75rem;
}

.home-view__birthday-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 999px;
  background: rgba(255, 252, 247, 0.92);
  color: #6f5b36;
  border: 1px solid rgba(111, 91, 54, 0.18);
}

.home-view__birthday-trigger.has-alert {
  background: linear-gradient(135deg, rgba(255, 244, 214, 0.95), rgba(255, 235, 194, 0.9));
  border-color: rgba(179, 130, 44, 0.22);
  color: #7b5522;
}

.home-view__birthday-trigger-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
}

.home-view__birthday-trigger-copy {
  font-weight: 700;
}

.home-view__birthday-trigger-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.6rem;
  height: 1.6rem;
  padding: 0 0.35rem;
  border-radius: 999px;
  background: rgba(123, 85, 34, 0.14);
  font-size: 0.78rem;
  font-weight: 700;
}

.home-export-modal__card {
  max-width: 34rem;
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.42);
  box-shadow: 0 28px 60px rgba(32, 24, 16, 0.22);
}

.home-export-modal {
  z-index: 700;
}

.home-birthday-modal {
  z-index: 700;
}

.home-birthday-modal__card {
  max-width: 42rem;
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.42);
  box-shadow: 0 28px 60px rgba(32, 24, 16, 0.22);
}

.home-birthday-modal__head {
  align-items: flex-start;
  background: linear-gradient(180deg, #fff8ee, #f4e6d3);
  border-bottom: 1px solid rgba(131, 92, 48, 0.16);
}

.home-birthday-modal__title {
  color: #2b1f14;
}

.home-birthday-modal__body {
  background: #fffdf8;
}

.home-export-modal__head {
  align-items: flex-start;
  background: linear-gradient(180deg, #fff8ee, #f4e6d3);
  border-bottom: 1px solid rgba(131, 92, 48, 0.16);
}

.home-export-modal__kicker {
  margin: 0 0 0.3rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16rem;
  text-transform: uppercase;
  color: #8a6030;
}

.home-export-modal__title {
  color: #2b1f14;
}

.home-export-modal__body {
  background: #fffdf8;
}

.home-export-modal__copy {
  margin: 0;
  color: #31261b;
  line-height: 1.7;
}

.home-export-modal__copy + .home-export-modal__copy {
  margin-top: 0.85rem;
}

.home-export-modal__copy--muted {
  color: rgba(49, 38, 27, 0.72);
}

.home-export-modal__textarea {
  margin-top: 1rem;
  border-radius: 18px;
  border: 1px solid rgba(131, 92, 48, 0.18);
  background: rgba(255, 255, 255, 0.88);
  color: #31261b;
}

.home-export-modal__input {
  margin-top: 0.85rem;
  border-radius: 18px;
  border: 1px solid rgba(131, 92, 48, 0.18);
  background: rgba(255, 255, 255, 0.88);
  color: #31261b;
}

.home-export-modal__error {
  margin: 0.65rem 0 0;
  color: #b95252;
}

.home-export-modal__foot {
  justify-content: flex-end;
  gap: 0.75rem;
  background: #fff7ec;
  border-top: 1px solid rgba(131, 92, 48, 0.14);
}

.home-export-modal__danger {
  border-radius: 999px;
  background: linear-gradient(135deg, #9f4a22, #c96d2d);
  border: none;
  color: #fff8f2;
}

.home-export-modal__secondary {
  border-radius: 999px;
}

.home-view__págination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.25rem;
  padding: 1rem 1.1rem;
  border: 1px solid rgba(255, 255, 255, 0.46);
  border-radius: 22px;
  background: rgba(255, 252, 247, 0.7);
}

.home-view__págination-copy {
  margin: 0;
  color: rgba(36, 31, 25, 0.7);
}

.home-view__págination-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.home-view__page-button {
  min-width: 96px;
  border-radius: 999px;
}

.home-view__page-indicator {
  min-width: 120px;
  text-align: center;
  font-size: 0.92rem;
  color: #211c17;
}

@media (max-width: 640px) {
  .home-view__quality-grid {
    grid-template-columns: 1fr;
  }

  .home-view__birthday-head,
  .home-view__birthday-item,
  .home-view__birthday-notice {
    flex-direction: column;
    align-items: flex-start;
  }

  .home-view__birthday-item-meta {
    align-items: flex-start;
  }

  .home-view__title {
    font-size: 2rem;
  }

  .home-view__subtitle {
    font-size: 0.95rem;
  }

  .home-view__filters-grid {
    grid-template-columns: 1fr;
  }

  .home-view__status-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .home-view__advanced-toggle {
    white-space: normal;
    text-align: left;
  }

  .home-view__actions-row {
    flex-direction: column;
    align-items: stretch;
  }

  .home-view__toolbar-actions {
    width: 100%;
    justify-content: stretch;
  }

  .home-view__export-actions {
    width: 100%;
    justify-content: stretch;
  }

  .home-view__birthday-trigger,
  .home-view__export-button {
    width: 100%;
  }

  .home-view__págination {
    flex-direction: column;
    align-items: stretch;
  }

  .home-view__págination-actions {
    justify-content: space-between;
  }

  .home-view__page-indicator {
    min-width: auto;
  }
}

@media (max-width: 480px) {
  .home-view {
    padding-top: 0.85rem;
  }

  .home-view__search-wrap {
    margin-top: 1rem;
  }

  .home-view__advanced-panel {
    padding: 0.8rem;
    border-radius: 18px;
  }

  .home-view__status-chip {
    width: 100%;
    justify-content: center;
  }

  .home-view__págination-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .home-view__page-button {
    width: 100%;
  }
}
</style>


